import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tag, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

const ActiveOffersCard = () => {
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
  });

  const { data: activeOffers, isLoading } = useQuery({
    queryKey: ['active-offers', session?.user?.id],
    queryFn: async () => {
      if (!session?.user) return [];
      
      const { data, error } = await supabase
        .from('partner_code_redemptions')
        .select(`
          id,
          redeemed_at,
          partner_codes!inner(
            code,
            partner_name,
            description,
            discount_percentage,
            valid_until,
            is_active,
            applies_to_tags
          )
        `)
        .eq('user_id', session.user.id)
        .eq('redemption_type', 'percentage')
        .eq('partner_codes.is_active', true)
        .gte('partner_codes.valid_until', new Date().toISOString());
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!session?.user,
  });

  if (isLoading || !activeOffers || activeOffers.length === 0) {
    return null;
  }

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="text-primary w-5 h-5" />
        <h2 className="text-lg font-semibold text-foreground">Active Offers</h2>
      </div>
      
      <div className="space-y-3">
        {activeOffers.map((offer) => {
          const partnerCode = offer.partner_codes;
          return (
            <div 
              key={offer.id} 
              className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 bg-secondary/10 rounded-lg"
            >
              <div className="flex-1">
                <p className="font-medium text-sm">{partnerCode.description || partnerCode.partner_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {partnerCode.partner_name}
                </p>
                {partnerCode.valid_until && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    <span>Until {format(new Date(partnerCode.valid_until), "MMM d, yyyy")}</span>
                  </div>
                )}
              </div>
              <Badge variant="secondary" className="text-base self-start sm:self-center bg-secondary text-white">
                {partnerCode.discount_percentage}% off
              </Badge>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default ActiveOffersCard;
